import { createReducer, on } from '@ngrx/store';
import { CallStatus } from '../../models/call-status.enum';
import { NpoSearchResults } from '../../models/npo-search-results.model';
import { Npo } from '../../models/npo.model';
import {
    activateSuccess,
    enrollmentStatusError,
    enrollmentStatusNotFound,
    enrollmentStatusSuccess,
    getEnrollmentStatus,
    npoSearched,
    npoSearchError,
    npoSearchModalOpened,
    npoSearchSuccess,
    npoSelected,
    pageChanged,
    partnershipChanged,
    postNpoSearch,
    returnedFromStripe,
    reviewSectionClosed,
    showErrorsIfClean,
    successPageExited,
} from '../actions/dpp-enrollment.actions';
import { initialState } from '../state/dpp-enrollment.state';

export const dppEnrollmentReducer = createReducer(
    initialState,
    //// UI actions
    on(returnedFromStripe, (state, action) => ({
        ...state,
        returnedFromStripe: true,
        enrollmentStatus: {
            digitalPayeeId: action.digitalPayeeId,
        },
    })),
    on(npoSearchModalOpened, (state) => ({
        ...state,
        npoSearchResults: null,
        npoSearchCallStatus: CallStatus.INITIAL,
    })),
    on(npoSearched, (state, action) => ({
        ...state,
        npoSearchRequest: {
            ein: action.ein,
            pageNumber: 1,
        },
        npoSearchResults: null,
    })),
    on(pageChanged, (state, action) => ({
        ...state,
        npoSearchRequest: {
            ein: state.npoSearchRequest!.ein,
            pageNumber: action.pageNumber,
        },
        npoSearchResults: null,
    })),
    on(npoSelected, (state, action) => ({
        ...state,
        enrollmentStatus: null,
        showErrorsIfClean: false,
        selectedNpo: updatePartnershipForACH(action.npo),
        hasSecondaries: Boolean(
            !action.npo.secondaryCharityId &&
                state.npoSearchResults?.items.find(
                    (item) => item.secondaryCharityId && item.charityId === action.npo.charityId
                )
        ),
    })),
    on(reviewSectionClosed, successPageExited, (state) => ({
        ...state,
        selectedNpo: null,
        hasSecondaries: false,
        showErrorsIfClean: false,
    })),
    on(partnershipChanged, (state, action) => ({
        ...state,
        partnershipChanged: action.isPartnership,
    })),
    on(showErrorsIfClean, (state) => ({
        ...state,
        showErrorsIfClean: true,
    })),
    //// API actions
    // NPO search
    on(postNpoSearch, (state) => ({
        ...state,
        npoSearchCallStatus: CallStatus.IN_PROGRESS,
        npoSearchResults: null,
    })),
    on(npoSearchSuccess, (state, action) => ({
        ...state,
        npoSearchCallStatus: CallStatus.SUCCESS,
        npoSearchResults: processNpoSearchResults(action.npoSearchResults),
    })),
    on(npoSearchError, (state) => ({
        ...state,
        npoSearchCallStatus: CallStatus.ERROR,
        npoSearchResults: null,
    })),
    // Enrollment status
    on(getEnrollmentStatus, (state) => ({
        ...state,
        enrollmentStatusCallStatus: CallStatus.IN_PROGRESS,
    })),
    on(enrollmentStatusSuccess, (state, action) => ({
        ...state,
        enrollmentStatusCallStatus: CallStatus.SUCCESS,
        enrollmentStatus: action.enrollmentStatus,
    })),
    on(enrollmentStatusNotFound, (state) => ({
        ...state,
        enrollmentStatusCallStatus: CallStatus.NOT_FOUND,
        enrollmentStatus: null,
    })),
    on(enrollmentStatusError, (state) => ({
        ...state,
        enrollmentStatusCallStatus: CallStatus.ERROR,
        enrollmentStatus: null,
    })),
    // Activate
    on(activateSuccess, (state, action) => ({
        ...state,
        enrollmentStatus: action.enrollmentStatus,
    }))
);

const processNpoSearchResults = (results: NpoSearchResults) => {
    return sortByCharityType(chooseAddress(results));
};

const chooseAddress = (results: NpoSearchResults) => {
    const items = [...results.items].map((item) => {
        if (item.addresses.length > 1) {
            const primaryAddress =
                item.addresses.find((addr) => addr.isPrimary) || item.addresses[0];
            return {
                ...item,
                addresses: [primaryAddress],
            };
        } else {
            return item;
        }
    });

    return {
        ...results,
        items,
    };
};

const sortByCharityType = (results: NpoSearchResults) => {
    const items = [...results.items].sort((a, b) => {
        const aVal = a.secondaryCharityId ? 1 : 0;
        const bVal = b.secondaryCharityId ? 1 : 0;

        return aVal - bVal;
    });

    return {
        ...results,
        items,
    };
};

const updatePartnershipForACH = (selectedNpo: Npo) => {
    if (selectedNpo.isACH) {
        return {
            ...selectedNpo,
            isPartner: true,
        };
    }
    return selectedNpo;
};
